import { keepPreviousData, useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';

import { FileFeature, FileResponse, FilesSort, FilesSortField, QueryFilesResponse } from '@playq/octopus2-files';
import { GenericFailure } from '@playq/services-shared';

import {
  IUseQueryOptionsExtended,
  useEitherQuery,
  useMutateQueryDataAfterEntityDeleting,
  useQueryPrefetch,
} from '/api/service-hooks';
import { services2 } from '/api/services2';
import { IQuery } from '/common/models';

import { filesQueryKeys } from './constants';

const emptyArray: [] = [];

export interface UseFilesQueryOptions
  extends IUseQueryOptionsExtended<QueryFilesResponse | undefined, GenericFailure | Error> {}

export const useFilesQuery = (
  features: FileFeature[],
  query: IQuery<FilesSortField, FilesSort>,
  options?: UseFilesQueryOptions
) => {
  const queryClient = useQueryClient();

  const keys = useMemo(
    () => filesQueryKeys.concat(features, query.iterator, query.sortBy, query.filterBy),
    [features, query.filterBy, query.sortBy, query.iterator]
  );

  const { mutate, refetch, ...res } = useEitherQuery(
    keys,
    () => services2.filesService.queryFiles(query.iterator, features, query.sortBy, query.filterBy),
    {
      placeholderData: keepPreviousData,
      ...options,
    }
  );

  const total = useMemo(() => res.data?.total ?? 0, [res.data?.total]);

  const { nextKeys } = useQueryPrefetch({
    keys,
    enabled: options?.enablePrefetch,
    total,
    args: [query.iterator, features, query.sortBy, query.filterBy],
    serviceMethod: services2.filesService.queryFiles.bind(services2.filesService),
  });

  const mutateDeletedFile = useMutateQueryDataAfterEntityDeleting({
    queryClient,
    nextPageQueryKey: nextKeys,
    total,
    iterator: query.iterator,
    mutate,
    refetch,
    getID: (fR?: FileResponse) => fR?.file?.id,
    getEntities: (r: QueryFilesResponse) => r.entities,
    setEntities: (r: QueryFilesResponse, newEntities: FileResponse[]) => {
      r.entities = newEntities;
    },
    createNewResponse: (prevResponse: QueryFilesResponse) => new QueryFilesResponse(prevResponse.serialize()),
  });

  const removeQueries = useCallback(() => {
    queryClient.removeQueries({ queryKey: filesQueryKeys });
  }, [queryClient]);

  return {
    ...res,
    files: res.data?.entities ?? emptyArray,
    total,
    tags: keys,
    mutateDeletedFile,
    removeQueries,
    refetch,
  };
};
